import type MarkdownIt from 'markdown-it'
import type { RenderRule } from 'markdown-it/lib/renderer'
import type TurndownService from 'turndown'
import type { Editor } from 'src/pages/datastores/document-detail/paragraph/editor/index.ts'
import SplitMark from '@/pages/datastores/document-detail/paragraph/SplitMark.tsx'
import ImageLoadFail from '@/assets/dataset/paragraph/image-load-fail.svg?url'
import { RenderReactComponent } from './dom.tsx'

function withSplitMarkRenderRule(
  tokens: any[],
  idx: number,
  options: any,
  env: any,
  self: any,
  defaultRender: RenderRule,
) {
  // 使用默认渲染器渲染分段开头
  const result = defaultRender(tokens, idx, options, env, self)

  // 检查是否是最后一块
  const isLastParagraph = idx === tokens.length - 1

  if (!isLastParagraph && tokens[idx].level === 0) {
    // 在非最后一块后添加分割线
    return `
        ${result}
        <p class="paragraph-split-mark"></p>
      `
  } else {
    return result
  }
}

export function splitParagraphPlugin(md: MarkdownIt) {
  const blocks = [
    'paragraph_close',
    'heading_close',
    'fence',
    'blockquote_close',
    'image',
    'video',
    'ordered_list_close',
    'bullet_list_close',
  ]

  const defaultRender = (
    tokens: any[],
    idx: number,
    options: any,
    _env: string,
    self: any,
  ) => {
    return self.renderToken(tokens, idx, options)
  }

  blocks.forEach(block => {
    const defaultBlockRender = md.renderer.rules[block] || defaultRender
    md.renderer.rules[block] = function (tokens, idx, options, env, self) {
      return withSplitMarkRenderRule(
        tokens,
        idx,
        options,
        env,
        self,
        defaultBlockRender,
      )
    }
  })
}

export function withTurndownRule(turndown: TurndownService) {
  turndown
    .addRule('code', {
      filter: ['pre'],
      replacement(content) {
        return `\n\`\`\`\n${content}\`\`\`\n`
      },
    })
    .addRule('p', {
      filter: ['p'],
      replacement(content) {
        const markdownReplacementsReverse = [[/\\_/g, '_']]

        const unescapeMarkdown = (text: string) =>
          markdownReplacementsReverse.reduce(
            (search, replacement) =>
              search.replace(replacement[0], replacement[1] as string),
            text,
          )
        const newContent = unescapeMarkdown(content)
        return newContent
      },
    })
    .addRule('image', {
      filter: 'img',
      replacement(_content: string, node: unknown) {
        const alt = (node as HTMLImageElement).alt || ''
        const src = (node as HTMLImageElement).getAttribute('src') || ''
        const ruleSrc = (node as HTMLImageElement).getAttribute('realUrl')
        return `![${alt}]` + `(${ruleSrc || src})`
      },
    })
    .addRule('video', {
      filter: 'video',
      replacement(_content: string, node: unknown) {
        const src = (node as HTMLImageElement).getAttribute('src') || ''
        return `&nbsp<video contenteditable="true" controls src="${src}"></video>&nbsp`
      },
    })
}

export function withCustomRender(instance: Editor) {
  if (instance.editor) {
    const splitMarks =
      instance.editor.querySelectorAll<HTMLParagraphElement>(
        '.paragraph-split-mark',
      ) ?? []
    splitMarks.forEach(splitMark => {
      RenderReactComponent(SplitMark, splitMark)
    })

    const imageElements =
      instance.editor.querySelectorAll<HTMLImageElement>('img')
    imageElements.forEach(image => {
      image.addEventListener('error', () => {
        image.setAttribute('realUrl', image.src)
        image.src = ImageLoadFail
      })
    })
  }
}

export function withClickEvent(instance: Editor) {
  const editor = instance.editor
  if (editor) {
    editor.addEventListener('click', e => {
      if (e.target instanceof HTMLAnchorElement && (e.metaKey || e.ctrlKey)) {
        if (instance.listener.onLinkOpen) {
          instance.listener.onLinkOpen(e.target.href)
        } else {
          window.open(e.target.href, '_blank')
        }
      }
      if (e.target instanceof HTMLImageElement) {
        instance.listener.onImagePreview?.(e.target.src)
      }
    })
  }
}

export function withLinkEdit(instance: Editor) {
  const editor = instance.editor
  if (editor) {
    function dealWithLinkEdit() {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) return

      const range = selection.getRangeAt(0)
      const selectedElement = range.commonAncestorContainer.parentElement
      if (selectedElement && selectedElement.tagName === 'A') {
        const url = selectedElement.getAttribute('href')
        console.log('withLinkEdit:::', url, selectedElement.textContent)
      }
    }
    editor.addEventListener('click', dealWithLinkEdit)
    editor.addEventListener('keydown', e => {
      if (['ArrowUp', 'ArrowDown'].includes(e.code)) {
        setTimeout(() => {
          dealWithLinkEdit()
        }, 0)
      }
    })
  }
}
